import { useState } from 'react';
import { Input, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import type { Qty } from '@app/shared';
import { useReportInvalid } from './invalidInputs';
import { parseQtyInput } from './parse';
import type { QtyParseOptions } from './parse';

export interface QtyInputProps extends QtyParseOptions {
  value: Qty | null;
  onChange: (value: Qty | null) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: React.Ref<InputRef>;
  /** LinesGrid keyboard navigation (§1 «Tab и Enter переходят по полям») — см. useGridNavigation. */
  navRow?: number;
  navCol?: number;
}

/**
 * Количество в штуках — каноническая строка («4.3», «10»), до `decimals` знаков после запятой.
 * Запятая трактуется как точка. Коммит на blur/Enter, не на каждый символ (§1). Неверный ввод не
 * исправляется молча: текст остаётся в поле, поле краснеет и объясняет причину, наружу уходит
 * «пусто» — документ с таким полем не провести и не сохранить как заполненный.
 */
export function QtyInput({ value, onChange, positive, max, decimals, autoFocus, disabled, onKeyDown, inputRef, navRow, navCol }: QtyInputProps) {
  const [draft, setDraft] = useState(value ?? '');
  const [error, setError] = useState<string | null>(null);
  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    // См. IntInput: собственный отказ не стирает введённый текст.
    if (!(value === null && error !== null)) {
      setDraft(value ?? '');
      setError(null);
    }
  }

  useReportInvalid(error !== null && !disabled);

  const commit = () => {
    const parsed = parseQtyInput(draft, { positive, max, decimals });
    setError(parsed.error);
    if (parsed.error === null) setDraft(parsed.value ?? '');
    if (parsed.value !== value) onChange(parsed.value);
  };

  const classes = [navRow !== undefined ? 'nav-cell' : '', error ? 'input-invalid' : ''].filter(Boolean).join(' ');

  return (
    <Tooltip title={error ?? ''} open={error ? undefined : false} color="#cf1322">
      <Input
        ref={inputRef}
        value={draft}
        inputMode="decimal"
        autoFocus={autoFocus}
        disabled={disabled}
        status={error ? 'error' : undefined}
        aria-invalid={error ? true : undefined}
        className={classes || undefined}
        data-nav-row={navRow}
        data-nav-col={navCol}
        style={{ textAlign: 'right', width: '100%' }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          onKeyDown?.(e);
        }}
      />
    </Tooltip>
  );
}
